import React, { useState, useEffect, useRef } from 'react';
import { 
    XIcon, GlobeIcon, UsersIcon, MailIcon, 
    ImageIcon, VideoIcon, AudioIcon, ExternalLinkIcon,
    ChevronRightIcon, SearchIcon, SendIcon
} from './Icons';
import { Community, UserData, MediaAttachment } from '../types';
import { FormattedText } from './FormattedText';
import { useMentionSearch } from '../hooks/useMentionSearch';
import { MentionDropdown } from './MentionDropdown';

interface ShareToGoodLyfeModalProps {
    isOpen: boolean;
    onClose: () => void;
    sharedData: {
        text?: string;
        url?: string;
        files?: { url: string; type: 'image' | 'video' | 'audio' }[];
    } | null;
    communities: Community[];
    usersData: Record<string, UserData>;
    currentUser: string | null;
    onShare: (params: {
        destination: 'profile' | 'community' | 'dm';
        communityId?: string;
        channelId?: string;
        recipientId?: string;
        text: string;
        media: MediaAttachment[];
    }) => void;
}

export const ShareToGoodLyfeModal = ({
    isOpen,
    onClose,
    sharedData,
    communities,
    usersData,
    currentUser,
    onShare
}: ShareToGoodLyfeModalProps) => {
    const [destination, setDestination] = useState<'profile' | 'community' | 'dm'>('profile');
    const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
    const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
    const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
    const [text, setText] = useState(sharedData?.text || sharedData?.url || '');
    const [media, setMedia] = useState<MediaAttachment[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();

    useEffect(() => {
        if (sharedData) {
            setText(sharedData.text || sharedData.url || '');
            if (sharedData.files) {
                setMedia(sharedData.files.map(f => ({
                    url: f.url,
                    type: f.type
                })));
            }
        }
    }, [sharedData]);


    if (!isOpen || !sharedData) return null;

    const handleShare = () => {
        onShare({
            destination,
            communityId: selectedCommunity?._id,
            channelId: selectedChannel || undefined,
            recipientId: selectedRecipient || undefined,
            text,
            media
        });
        onClose();
    };


    const filteredCommunities = communities.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredRecipients = Object.entries(usersData)
        .filter(([username]) => username !== currentUser)
        .map(([username, data]) => ({ ...data, username }))
        .filter(u => u.username.toLowerCase().includes(searchQuery.toLowerCase()));


    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-gray-900 border border-gray-800 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <SendIcon className="w-5 h-5 text-blue-500" />
                        Share to GoodLyfe
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content Preview & Editor */}
                <div className="flex-grow overflow-y-auto p-4 space-y-6">
                    <div className="space-y-4">
                        <textarea
                            value={text}
                            onChange={e => {
                                const val = e.target.value;
                                setText(val);
                                updateSearch(val, e.target.selectionStart);
                            }}
                            placeholder="Add a caption..."
                            className="w-full bg-gray-800/50 rounded-2xl p-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none min-h-[120px]"
                        />
                        {mentionQuery !== null && (
                            <div className="relative">
                                <MentionDropdown
                                    query={mentionQuery}
                                    following={usersData[currentUser || '']?.following || []}
                                    usersData={usersData}
                                    onSelect={(username) => {
                                        const { newText, newCursorPos } = completeMention(text, username, text.length);
                                        setText(newText);
                                        setTimeout(() => {
                                            const el = document.querySelector('textarea') as HTMLTextAreaElement;
                                            if (el) {
                                                el.focus();
                                                el.setSelectionRange(newCursorPos, newCursorPos);
                                            }
                                        }, 10);
                                    }}
                                    position="below"
                                />
                            </div>
                        )}

                        {media.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
                                {media.map((item, idx) => (
                                    <div key={item.url} className="relative flex-shrink-0 w-32 h-32 rounded-xl overflow-hidden group">

                                        {item.type === 'image' ? (
                                            <img src={item.url} alt="" className="w-full h-full object-cover" />
                                        ) : item.type === 'video' ? (
                                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                                <VideoIcon className="w-8 h-8 text-gray-400" />
                                            </div>
                                        ) : (
                                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                                <AudioIcon className="w-8 h-8 text-gray-400" />
                                            </div>
                                        )}
                                        <button 
                                            onClick={() => setMedia(prev => prev.filter((_, i) => i !== idx))}
                                            className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-black/80 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <XIcon className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Destination Selection */}
                    <div className="space-y-4">
                        <label className="text-sm font-bold text-gray-400 px-1">SELECT DESTINATION</label>
                        <div className="flex p-1 bg-gray-800/50 rounded-2xl">
                            {(['profile', 'community', 'dm'] as const).map(dt => (
                                <button
                                    key={dt}
                                    onClick={() => {
                                        setDestination(dt);
                                        setSearchQuery('');
                                    }}
                                    className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
                                        destination === dt 
                                        ? 'bg-blue-600 text-white shadow-lg' 
                                        : 'text-gray-400 hover:text-white'
                                    }`}
                                >
                                    {dt.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        {/* Destination Sub-options */}
                        <div className="space-y-4 animate-fade-in">
                            {destination === 'profile' && (
                                <div className="p-4 bg-gray-800/30 rounded-2xl border border-gray-800/50 flex items-center gap-4">
                                    <div className="p-3 bg-blue-500/10 rounded-xl">
                                        <GlobeIcon className="w-6 h-6 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-white">Your Profile Feed</p>
                                        <p className="text-xs text-gray-400">Share this post to your main profile</p>
                                    </div>
                                </div>
                            )}

                            {destination === 'community' && (
                                <div className="space-y-3">
                                    {!selectedCommunity ? (
                                        <>
                                            <div className="relative">
                                                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                                <input
                                                    type="text"
                                                    value={searchQuery}
                                                    onChange={e => setSearchQuery(e.target.value)}
                                                    placeholder="Search communities..."
                                                    className="w-full bg-gray-800/50 rounded-2xl py-3 pl-10 pr-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                                {filteredCommunities.map(c => (
                                                    <button
                                                        key={c._id}

                                                        onClick={() => {
                                                            setSelectedCommunity(c);
                                                            setSelectedChannel(c.channels?.[0]?.id || 'general');
                                                        }}
                                                        className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl transition-colors group text-left"
                                                    >
                                                        <div className="w-10 h-10 bg-gray-700 rounded-xl overflow-hidden">
                                                            {c.avatar ? <img src={c.avatar} className="w-full h-full object-cover" alt="" /> : <UsersIcon className="w-full h-full p-2 text-gray-500" />}
                                                        </div>

                                                        <div className="flex-grow">
                                                            <p className="font-bold text-white group-hover:text-blue-400 transition-colors text-sm">{c.name}</p>
                                                            <p className="text-[10px] text-gray-500 truncate">{c.description}</p>
                                                        </div>
                                                        <ChevronRightIcon className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="p-3 bg-gray-800/30 rounded-2xl border border-gray-800/50 space-y-3">
                                            <div className="flex items-center gap-3 pb-3 border-b border-gray-800/50">
                                                <div className="w-8 h-8 bg-gray-700 rounded-lg overflow-hidden">
                                                    {selectedCommunity.avatar ? <img src={selectedCommunity.avatar} className="w-full h-full object-cover" alt="" /> : <UsersIcon className="w-full h-full p-1.5 text-gray-500" />}
                                                </div>

                                                <span className="font-bold text-white text-sm">{selectedCommunity.name}</span>
                                                <button onClick={() => setSelectedCommunity(null)} className="ml-auto text-xs text-blue-500 font-bold hover:underline">Change</button>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {(selectedCommunity.channels || [{id: 'general', name: 'General'}]).map(ch => (
                                                    <button
                                                        key={ch.id}
                                                        onClick={() => setSelectedChannel(ch.id)}
                                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                                                            selectedChannel === ch.id 
                                                            ? 'bg-blue-600 border-blue-600 text-white' 
                                                            : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                                                        }`}
                                                    >
                                                        # {ch.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {destination === 'dm' && (
                                <div className="space-y-3">
                                    {!selectedRecipient ? (
                                        <>
                                            <div className="relative">
                                                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                                <input
                                                    type="text"
                                                    value={searchQuery}
                                                    onChange={e => setSearchQuery(e.target.value)}
                                                    placeholder="Search recipients..."
                                                    className="w-full bg-gray-800/50 rounded-2xl py-3 pl-10 pr-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                                {filteredRecipients.map(u => (
                                                    <button
                                                        key={u.username}
                                                        onClick={() => setSelectedRecipient(u.username)}
                                                        className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl transition-colors group text-left"
                                                    >
                                                        <img src={u.pfp} className="w-10 h-10 rounded-full object-cover" alt="" />
                                                        <div className="flex-grow">
                                                            <p className="font-bold text-white group-hover:text-blue-400 transition-colors text-sm">{u.username}</p>
                                                            <p className="text-[10px] text-gray-400">@{u.username.toLowerCase()}</p>
                                                        </div>
                                                        <ChevronRightIcon className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />
                                                    </button>
                                                ))}

                                            </div>
                                        </>
                                    ) : (
                                        <div className="p-4 bg-gray-800/30 rounded-2xl border border-gray-800/50 flex items-center gap-4">
                                            <img src={usersData[selectedRecipient]?.pfp} className="w-12 h-12 rounded-full object-cover" alt="" />
                                            <div>
                                                <p className="font-bold text-white">{selectedRecipient}</p>
                                                <button onClick={() => setSelectedRecipient(null)} className="text-xs text-blue-500 font-bold hover:underline text-left block">Change Recipient</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-gray-800 flex items-center gap-3">
                    <button
                        onClick={handleShare}
                        disabled={
                            (destination === 'community' && !selectedCommunity) ||
                            (destination === 'dm' && !selectedRecipient)
                        }
                        className="flex-grow bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600 text-white font-bold py-4 rounded-2xl transition-all shadow-xl shadow-blue-900/20 active:scale-95 flex items-center justify-center gap-2"
                    >
                        <SendIcon className="w-5 h-5" />
                        Share to GoodLyfe
                    </button>
                </div>
            </div>
        </div>
    );
};
